import type { Routine, RoutineDay } from '@fitness/api-client';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Copy,
  Eye,
  Pencil,
  Play,
  Share2,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { Badge } from '@/components/exercise/Chip';
import { ExerciseThumb } from '@/components/exercise/ExerciseThumb';
import { CrossLink } from '@/components/routine/CrossLink';
import { prescriptionLine } from '@/components/routine/templateFormat';
import { describeError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ACTIVE_ROUTINE, DAY_PREVIEW, visibilityLabel } from '@/lib/i18n/training';
import {
  useArchiveRoutine,
  useDeleteRoutine,
  useForkRoutine,
  useRoutine,
  useRoutineAccount,
  useSetActiveRoutine,
} from '@/lib/queries/routines';

export const Route = createFileRoute('/_app/routines/$id/')({
  component: RoutineDetailPage,
});

function RoutineDetailPage() {
  const { id } = Route.useParams();
  const query = useRoutine(id);

  return (
    <>
      <PageHeader
        title={query.data?.name ?? 'Rutina'}
        action={
          <Link
            to="/routines"
            search={{ archived: false }}
            aria-label="Volver a mis rutinas"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      {query.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando la rutina…</span>
        </div>
      ) : query.isError ? (
        <ErrorBanner
          title="No hemos podido cargar la rutina"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <RoutineDetail routine={query.data} />
      )}
    </>
  );
}

function RoutineDetail({ routine }: { routine: Routine }) {
  const { user } = useAuth();
  const account = useRoutineAccount();
  const isActive = account.data?.activeRoutineId === routine.id;
  const isMine = routine.ownerId === user?.id;
  const archived = routine.archivedAt !== null;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-1.5">
        <Badge tone={routine.visibility === 'private' ? 'plain' : 'brand'}>
          {visibilityLabel(routine.visibility)}
        </Badge>
        <Badge>
          {routine.days.length} {routine.days.length === 1 ? 'día' : 'días'}
        </Badge>
        {routine.forkedFrom !== null ? <Badge>Copia de otra rutina</Badge> : null}
        {archived ? <Badge tone="danger">Archivada</Badge> : null}
        {isActive ? <Badge tone="brand">{ACTIVE_ROUTINE.badge}</Badge> : null}
      </div>

      {routine.description !== null && routine.description !== '' ? (
        <p className="text-sm whitespace-pre-line text-ink-muted">{routine.description}</p>
      ) : null}

      {isMine ? (
        <OwnerActions routine={routine} archived={archived} isActive={isActive} />
      ) : (
        <ForkAction routineId={routine.id} />
      )}

      {routine.days.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-line bg-surface-raised p-6 text-center text-sm text-ink-muted">
          Esta rutina todavía no tiene días.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {routine.days.map((day) => (
            <DaySection key={day.id} routineId={routine.id} day={day} />
          ))}
        </div>
      )}
    </div>
  );
}

function DaySection({ routineId, day }: { routineId: string; day: RoutineDay }) {
  return (
    <section className="rounded-2xl border border-line bg-surface-raised p-4">
      <header className="mb-3 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">{day.name}</h2>
          {day.notes !== null && day.notes !== '' ? (
            <p className="mt-1 text-sm text-ink-muted">{day.notes}</p>
          ) : null}
        </div>
        {/* La vista previa de Entrenar: prescripción, última marca y técnica,
            sin crear la sesión. */}
        <Link
          to="/train/day/$routineId/$dayId"
          params={{ routineId, dayId: day.id }}
          aria-label={DAY_PREVIEW.openLabel(day.name)}
          className="-my-1 inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl px-2 text-sm font-semibold text-brand transition-colors hover:bg-surface-overlay"
        >
          <Eye className="size-4" aria-hidden="true" />
          {DAY_PREVIEW.open}
        </Link>
      </header>

      {day.exercises.length === 0 ? (
        <p className="text-sm text-ink-muted">Sin ejercicios en este día.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {day.exercises.map((item) => (
            <li key={item.id}>
              {/* La fila entera lleva a la ficha: el nombre solo era una zona táctil
                  de una línea de texto. */}
              <Link
                to="/exercises/$id"
                params={{ id: item.exercise.id }}
                className="-mx-2 flex min-h-11 items-center gap-3 rounded-xl px-2 py-1 transition-colors hover:bg-surface-overlay active:bg-surface-overlay"
              >
                <ExerciseThumb media={item.exercise.media} name={item.exercise.nameEs} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-sm font-medium text-ink">
                    {item.exercise.nameEs}
                  </span>
                  <span className="mt-0.5 block text-xs text-ink-muted">
                    {prescriptionLine(item)}
                  </span>
                  {item.supersetGroup !== null ? (
                    <span className="mt-0.5 block text-xs text-brand">
                      Superserie {item.supersetGroup + 1}
                    </span>
                  ) : null}
                  {item.notes !== null && item.notes !== '' ? (
                    <span className="mt-0.5 block text-xs text-ink-faint">{item.notes}</span>
                  ) : null}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/* `/train/session/new` la construye el módulo de entrenamiento. */}
      <CrossLink
        to="/train/session/new"
        search={{ routineId, routineDayId: day.id }}
        className="mt-3 flex min-h-11 items-center justify-center gap-2 rounded-xl bg-brand px-4 font-semibold text-brand-ink"
      >
        <Play className="size-4" aria-hidden="true" />
        Empezar este día
      </CrossLink>
    </section>
  );
}

function OwnerActions({
  routine,
  archived,
  isActive,
}: {
  routine: Routine;
  archived: boolean;
  isActive: boolean;
}) {
  const navigate = useNavigate();
  const archive = useArchiveRoutine();
  const remove = useDeleteRoutine();
  const setActive = useSetActiveRoutine();
  const [confirming, setConfirming] = useState(false);
  const failure = archive.error ?? remove.error ?? setActive.error;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {!archived ? (
          <Button
            variant="secondary"
            data-tour="routine-follow"
            data-active={isActive ? 'true' : 'false'}
            loading={setActive.isPending}
            disabled={archive.isPending || remove.isPending}
            onClick={() => setActive.mutate(isActive ? null : routine.id)}
          >
            {isActive ? ACTIVE_ROUTINE.unfollow : ACTIVE_ROUTINE.follow}
          </Button>
        ) : null}
        <Link
          to="/routines/$id/edit"
          params={{ id: routine.id }}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-line bg-surface-raised px-4 text-sm font-semibold text-ink transition-colors hover:bg-surface-overlay"
        >
          <Pencil className="size-4" aria-hidden="true" />
          Editar
        </Link>
        <Button
          variant="secondary"
          loading={archive.isPending}
          icon={archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
          onClick={() => archive.mutate({ id: routine.id, archived })}
        >
          {archived ? 'Desarchivar' : 'Archivar'}
        </Button>
        <Link
          to="/community/compose"
          search={{ type: 'routine_share', routineId: routine.id }}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-line bg-surface-raised px-4 text-sm font-semibold text-ink transition-colors hover:bg-surface-overlay"
        >
          <Share2 className="size-4" aria-hidden="true" />
          Compartir en la comunidad
        </Link>
        {confirming ? null : (
          <Button
            variant="ghost"
            icon={<Trash2 className="size-4" />}
            onClick={() => setConfirming(true)}
          >
            Eliminar
          </Button>
        )}
      </div>

      {confirming ? (
        <div className="rounded-xl border border-danger/40 bg-danger-surface p-3">
          <p className="text-sm">
            ¿Seguro que quieres eliminar <strong>{routine.name}</strong>? Los entrenamientos que ya
            hayas registrado se conservan.
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() =>
                remove.mutate(routine.id, {
                  onSuccess: () => {
                    void navigate({ to: '/routines', search: { archived: false } });
                  },
                })
              }
            >
              Sí, eliminar
            </Button>
            <Button
              variant="secondary"
              disabled={remove.isPending}
              onClick={() => setConfirming(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : null}

      {failure ? (
        <p role="alert" className="text-sm text-danger">
          {describeError(failure)}
        </p>
      ) : null}
    </section>
  );
}

/** Rutina de otra persona: lo único que se puede hacer es copiarla. */
function ForkAction({ routineId }: { routineId: string }) {
  const navigate = useNavigate();
  const fork = useForkRoutine();

  return (
    <section className="flex flex-col gap-2">
      <Button
        loading={fork.isPending}
        icon={<Copy className="size-4" />}
        onClick={() =>
          fork.mutate(routineId, {
            onSuccess: (copy) => {
              void navigate({ to: '/routines/$id', params: { id: copy.id } });
            },
          })
        }
      >
        Guardar copia
      </Button>
      {fork.isError ? (
        <p role="alert" className="text-sm text-danger">
          {describeError(fork.error)}
        </p>
      ) : null}
    </section>
  );
}
